import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize, switchMap } from 'rxjs';
import { InternationalInvoiceDeclaration, PaymentCostsInfoArrayItem } from '@models';
import { formatCostForInternationalInvoice } from '@shared';
import { AlertToastService, AnalyticEventName, AnalyticsService, InvoiceService, MasterpassService } from '@services';
import { DeclarationPaymentData } from './declaration-payment.models';

@Component({
  selector: 'app-declaration-payment',
  templateUrl: './declaration-payment.component.html',
  styleUrls: ['./declaration-payment.component.scss'],
})
export class DeclarationPaymentComponent implements OnInit {
  isLoading: boolean = false;
  paymentPopupOpened: boolean = false;

  declarationData: InternationalInvoiceDeclaration | null = null;
  declarationPaymentCostsInfo: PaymentCostsInfoArrayItem = this.data.declarationPaymentCostsInfo;
  senderName: string = this.data.senderName;
  recipientPhone: string = this.data.recipientPhone;
  currencyCode: string = this.data.currencyCode;

  constructor(
    private invoiceService: InvoiceService,
    private masterpassService: MasterpassService,
    private dialogRef: MatDialogRef<DeclarationPaymentComponent>,
    private analyticsService: AnalyticsService,
    private alertToastService: AlertToastService,
    @Inject(MAT_DIALOG_DATA) private data: DeclarationPaymentData,
  ) {}

  ngOnInit(): void {
    this.getDeclarationInfo();
  }

  getDeclarationInfo(): void {
    this.isLoading = true;

    this.invoiceService
      .getDeclarations([this.declarationPaymentCostsInfo.Number])
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (declarations) => (this.declarationData = declarations[0] ?? null),
        error: (errors) => {
          if (errors) {
            this.alertToastService.pushApiErrors(errors);
          }
        },
      });
  }

  getFormattedCost(cost: number): string {
    return formatCostForInternationalInvoice(cost, this.currencyCode);
  }

  getSelectedServices(): Record<string, boolean> {
    return this.declarationPaymentCostsInfo.ServicesList.reduce<Record<string, boolean>>((acc, item) => {
      acc[item.OperationType] = item.Cost > 0;
      return acc;
    }, {});
  }

  initPayment(): void {
    if (this.paymentPopupOpened) {
      return;
    }

    this.analyticsService.track({ name: AnalyticEventName.CLICK_PAY });
    this.paymentPopupOpened = true;

    const docNumber = this.declarationPaymentCostsInfo.Number;
    const selectedServices = this.getSelectedServices();
    const invoiceType = 'in';

    this.masterpassService
      .initPayment(
        { docNumber, PhoneRecipient: this.recipientPhone },
        this.declarationPaymentCostsInfo,
        selectedServices,
        null,
        invoiceType,
      )
      .pipe(
        switchMap((data) => {
          const url = `${data.Url}&lang=ua`;
          return this.masterpassService.processPaymentV3(
            url,
            { docNumber, SendersPhone: this.recipientPhone },
            selectedServices,
            invoiceType,
            this.declarationPaymentCostsInfo,
            false
          );
        }),
        switchMap(() => this.masterpassService.getPaymentInfoArray([docNumber])),
        finalize(() => (this.paymentPopupOpened = false)),
      )
      .subscribe({
        next: (data) => {
          const paymentInfo = data?.[0];
          const isFullyPaid = paymentInfo?.InternationalDeliveryPaid === paymentInfo?.TotalInternationalDelivery;

          if (isFullyPaid) {
            this.dialogRef.close({ success: true });
          }
        },
        error: (errors) => {
          if (errors) {
            this.alertToastService.pushApiErrors(errors);
          }
        },
      });
  }
}
